import { create } from "zustand";
import axios from "../lib/axios.js";

export const useCartStore = create((set, get) => ({
  cartItems: [],
  wishlistItems: [],  
  loading: false,
  error: null,

  setCartItems: (cartItems) => set({ cartItems }),

   // ✅ Add to Wishlist
   addToWishlist: (product) => {
    set((state) => {
      const exists = state.wishlistItems.some((item) => item.id === product.id);
      if (!exists) {
        return { wishlistItems: [...state.wishlistItems, product] };
      }
      return state;
    });
  },

  // ✅ Remove from Wishlist
  removeFromWishlist: (product_id) => {
    set((state) => ({
      wishlistItems: state.wishlistItems.filter((item) => item.id !== product_id),
    }));
  },

  // ✅ Move to Cart
  moveToCart: async (product) => {
    try {
      await get().addToCart(product.id, product.quantity, product.size);
      get().removeFromWishlist(product.id);
      await get().getCartProducts();
    } catch (error) {
      console.error("Failed to move product to cart:", error.message);
    }
  },
  // ✅ Get Updated Cart Products
  getCartProducts: async () => {
    set({ loading: true, error: null });
    try {
      const response = await axios.get("/cart");
      const cartItems = Array.isArray(response.data)
        ? response.data.map((item) => ({
            ...item,
            id: Number(item.id),
            price: Number(item.price || 0),
            quantity: Number(item.quantity || 0),
            available_quantity: Number(item.available_quantity || 0),
          }))
        : [];

      set({ cartItems, loading: false });
      console.log("Cart Items:", response.data);
      return cartItems;
    } catch (error) {
      console.error(
        "Failed to fetch cart items:",
        error.response?.data || error.message
      );
      set({
        cartItems: error.response?.status === 401 ? [] : get().cartItems,
        loading: false,
        error: error.response?.data?.message || "Unable to load cart",
      });
      return get().cartItems;
    }
  },

  // ✅ Add to Cart (Now refreshes cart immediately)
  addToCart: async (product_id, quantity = 1, selectedSize) => {
    try {
      const size = selectedSize;
      const response = await axios.post("/cart", {
        product_id,
        quantity,
        size,
      });
      await get().getCartProducts(); // Refresh cart after adding item ✅
      console.log("Product added to cart:", response.data);
      return response.data;
    } catch (error) {
      console.error(
        "Failed to add product to cart:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  // ✅ Remove from Cart
  removeFromCart: async (product_id) => {
    try {
      await axios.delete("/cart", { data: { product_id } });
      await get().getCartProducts(); // Refresh cart ✅
      console.log("Product removed from cart");
    } catch (error) {
      console.error(
        "Failed to remove product from cart:",
        error.response?.data || error.message
      );
    }
  },

  // ✅ Update Cart Quantity in UI instantly
  updateCartQuantity: (product_id, quantity) => {
    set((state) => ({
      cartItems: state.cartItems.map((item) =>
        item.id === product_id ? { ...item, quantity } : item
      ),
    }));
  },

  // ✅ Update Cart Quantity (Sync with Backend)
  updateQuantity: async (product_id, quantity) => {
    try {
      await axios.put(`/cart/${product_id}`, { quantity });
      await get().getCartProducts(); // Refresh cart ✅
      console.log("Cart updated successfully");
      return true;
    } catch (error) {
      console.error(
        "Failed to update cart:",
        error.response?.data || error.message
      );
      await get().getCartProducts();
      throw error;
    }
  },

  // ✅ Update Cart Size
  updateSize: async (product_id, size) => {
    try {
      await axios.put(`/cart/${product_id}/size`, { size });
      await get().getCartProducts(); // Refresh cart ✅
      console.log("Cart size updated successfully");
    } catch (error) {
      console.error(
        "Failed to update cart size:",
        error.response?.data || error.message
      );
    }
  },

  // ✅ Clear Cart
  clearCart: async () => {
    try {
      await axios.delete("/cart");
      set({ cartItems: [] });
      console.log("Cart cleared successfully");
    } catch (error) {
      console.error(
        "Failed to clear cart:",
        error.response?.data || error.message
      );
    }
  },

  clearCartLocal: () => set({ cartItems: [] }),
}));
